import React, { useCallback, useEffect, useState, useRef } from 'react';
import { X, Check, Send, AlertCircle, Calendar, Truck, ShoppingBag, Clock, Building2, CreditCard, User, MessageSquare, Repeat, UtensilsCrossed, AlertTriangle, FileText, MapPin, Info } from 'lucide-react';
import { Button } from './ui/Button';
export type ServiceType = 'foretagsbestallning' | 'manadens_meny' | 'catering_event';
interface OrderFormProps {
  type: ServiceType;
  onClose: () => void;
}
const serviceConfig: Record<ServiceType, {
  title: string;
  subtitle: string;
  emoji: string;
}> = {
  foretagsbestallning: {
    title: 'Företagsbeställning',
    subtitle: 'Frukostpaket, luncher & fika till kontoret',
    emoji: '🏢'
  },
  manadens_meny: {
    title: 'Månadens meny',
    subtitle: 'Upptäck vad vi erbjuder just nu',
    emoji: '📋'
  },
  catering_event: {
    title: 'Catering & Event',
    subtitle: 'Mat & fika till fest, mingel och event',
    emoji: '🥂'
  }
};
const ALLERGIES = ['Glutenfri', 'Laktosfri', 'Nötfri', 'Vegansk', 'Vegetarisk', 'Äggfri', 'Annat'];
const CATERING_CATEGORIES = [{
  id: 'tarta',
  label: '🎂 Tårta',
  desc: 'Bröllopstårta, födelsedagstårta, dessertbord m.m.'
}, {
  id: 'bakelser',
  label: '🧁 Bakelser & konditori',
  desc: 'Petit fours, bakelser, kakor och annat fint fika'
}, {
  id: 'bullar',
  label: '🍞 Bullar & bröd',
  desc: 'Nybakade bullar, frallor, mackor och bröd'
}, {
  id: 'mat',
  label: '🍽️ Mat & lunch',
  desc: 'Sallader, wraps, smörgåstårta, buffé m.m.'
}, {
  id: 'fika',
  label: '☕ Fikapaket',
  desc: 'Komplett fika med blandning av bakverk och dryck'
}];
const WEB3FORMS_KEY = 'c9228efa-9300-46fa-a21c-050430afdc38';
// Replace with your uploaded menu image/PDF URL each month
const MANADENS_MENY_URL = '';
// Replace with your uploaded package info file URLs (image or PDF)
const PACKAGE_INFO_URLS: Record<string, string> = {
  frukost: '',
  lunch: '',
  fika: '' // URL to fikapaket info/prices
};
const inputClass = 'w-full h-11 px-4 rounded-lg border border-[#C5A55A]/20 bg-white focus:border-[#C5A55A] focus:ring-2 focus:ring-[#C5A55A]/20 outline-none transition-all font-sans text-sm';
const textareaClass = 'w-full min-h-[100px] p-4 rounded-lg border border-[#C5A55A]/20 bg-white focus:border-[#C5A55A] focus:ring-2 focus:ring-[#C5A55A]/20 outline-none transition-all font-sans resize-y text-sm';
const labelClass = 'block text-sm font-medium text-[#1A1A1A] mb-1.5';
const req = <span className="text-red-500 ml-0.5" data-id="element-687">*</span>;
const checkboxClass = 'flex items-center gap-2.5 p-2.5 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-[#FFF8F0] transition-colors has-[:checked]:bg-[#FFF8F0] has-[:checked]:border-[#C5A55A]';
const SectionHeading = ({
  icon: Icon,
  title
}: {
  icon: React.ElementType;
  title: string;
}) => <h4 className="font-serif text-lg text-[#1A1A1A] flex items-center gap-2 border-l-4 border-[#C5A55A] pl-3" data-id="element-688">
    <Icon className="w-5 h-5 text-[#C5A55A]" data-id="element-689" />
    {title}
  </h4>;
// Google Places Autocomplete hook
function useGooglePlaces(inputRef: React.RefObject<HTMLInputElement | null>, onSelect: (address: string) => void) {
  useEffect(() => {
    if (!inputRef.current) return;
    // Check if Google Maps is loaded
    if (typeof window !== 'undefined' && (window as any).google?.maps?.places) {
      const autocomplete = new (window as any).google.maps.places.Autocomplete(inputRef.current, {
        types: ['address'],
        componentRestrictions: {
          country: 'se'
        }
      });
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place?.formatted_address) {
          onSelect(place.formatted_address);
        }
      });
    }
  }, [inputRef, onSelect]);
}
export function OrderForm({
  type,
  onClose
}: OrderFormProps) {
  const config = serviceConfig[type];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const addressInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<Record<string, string>>({
    orgNummer: '',
    foretagsnamn: '',
    kontaktperson: '',
    email: '',
    phone: '',
    faktureringsEmail: '',
    bokningsReferens: '',
    orderType: '',
    paymentMethod: '',
    fakturaForetagsnamn: '',
    fakturaOrgNr: '',
    fakturaEmail: '',
    fakturaAdress: '',
    fakturaReferens: '',
    frequency: '',
    frequencyDays: '',
    deliveryType: 'Avhämtning',
    deliveryAddress: '',
    date: '',
    time: '',
    frukostAntal: '',
    frukostVariant: '',
    lunchAntal: '',
    lunchVariant: '',
    fikaAntal: '',
    fikaVariant: '',
    fikaPreferenser: '',
    fikaWantsDrink: '',
    fikaDrinkType: '',
    allergiAnnat: '',
    antalPersoner: '',
    eventType: '',
    cateringProjectMgmt: '',
    cateringProjectMgmtDetails: '',
    cateringHireStaff: '',
    cateringHireStaffDetails: '',
    cateringDrink: '',
    cateringDrinkDetails: '',
    details: ''
  });
  // Allergy state: which are selected + count per allergy
  const [selectedAllergies, setSelectedAllergies] = useState<string[]>([]);
  const [allergyCounts, setAllergyCounts] = useState<Record<string, string>>({});
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [categoryNotes, setCategoryNotes] = useState<Record<string, string>>({});
  const [activePackageInfo, setActivePackageInfo] = useState<string | null>(null);
  const isFöretag = type === 'foretagsbestallning';
  const isCatering = type === 'catering_event';
  const isMeny = type === 'manadens_meny';
  const isStående = formData.orderType === 'Stående bokning';
  const isEngångs = formData.orderType === 'Engångsbeställning';
  const isFaktura = formData.paymentMethod === 'Faktura';
  const isUtkörning = formData.deliveryType === 'Utkörning';
  const handleAddressSelect = useCallback((address: string) => {
    setFormData(prev => ({
      ...prev,
      deliveryAddress: address
    }));
  }, []);
  useGooglePlaces(addressInputRef, handleAddressSelect);
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const toggleAllergy = (allergy: string) => {
    setSelectedAllergies(prev => {
      if (prev.includes(allergy)) {
        // Remove allergy and its count
        const newCounts = {
          ...allergyCounts
        };
        delete newCounts[allergy];
        setAllergyCounts(newCounts);
        return prev.filter(a => a !== allergy);
      }
      return [...prev, allergy];
    });
  };
  const setAllergyCount = (allergy: string, count: string) => {
    setAllergyCounts(prev => ({
      ...prev,
      [allergy]: count
    }));
  };
  const toggleCategory = (cat: string) => {
    setSelectedCategories(prev => {
      if (prev.includes(cat)) {
        const newNotes = {
          ...categoryNotes
        };
        delete newNotes[cat];
        setCategoryNotes(newNotes);
        return prev.filter(c => c !== cat);
      }
      return [...prev, cat];
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    const payload: Record<string, string> = {
      access_key: WEB3FORMS_KEY,
      subject: `Ny förfrågan: ${config.title} – ${isFöretag ? formData.foretagsnamn : formData.kontaktperson}`,
      from_name: 'Top Notch Bakery Beställningar',
      replyto: formData.email,
      Typ: config.title
    };
    if (isFöretag) {
      payload['Organisationsnummer'] = formData.orgNummer;
      payload['Företagsnamn'] = formData.foretagsnamn;
      payload['Faktureringsmejl'] = formData.faktureringsEmail;
      payload['Referens för bokning'] = formData.bokningsReferens;
    }
    payload['Kontaktperson'] = formData.kontaktperson;
    payload['E-post'] = formData.email;
    payload['Telefon'] = formData.phone;
    if (!isMeny) {
      payload['Ordertyp'] = formData.orderType;
      if (isEngångs) {
        payload['Betalning'] = formData.paymentMethod;
        if (isFaktura && !isFöretag) {
          payload['Faktura – Företagsnamn'] = formData.fakturaForetagsnamn;
          payload['Faktura – Org.nr'] = formData.fakturaOrgNr;
          payload['Faktura – E-post'] = formData.fakturaEmail;
          payload['Faktura – Adress'] = formData.fakturaAdress;
          payload['Faktura – Referens'] = formData.fakturaReferens || '(Ej angivet)';
        }
      }
      if (isStående) {
        payload['Frekvens'] = formData.frequency;
        if (formData.frequencyDays) payload['Specifika dagar'] = formData.frequencyDays;
      }
      payload['Leveranssätt'] = formData.deliveryType;
      if (isUtkörning && formData.deliveryAddress) payload['Leveransadress'] = formData.deliveryAddress;
      payload['Önskat datum'] = formData.date;
      payload['Önskad tid'] = formData.time || '(Ej angivet)';
    }
    if (isFöretag) {
      if (formData.frukostAntal) {
        payload['Frukostpaket – antal'] = formData.frukostAntal;
        if (formData.frukostVariant) payload['Frukostpaket – variant'] = `Alternativ ${formData.frukostVariant}`;
      }
      if (formData.lunchAntal) {
        payload['Lunchpaket – antal'] = formData.lunchAntal;
        if (formData.lunchVariant) payload['Lunchpaket – variant'] = `Alternativ ${formData.lunchVariant}`;
      }
      if (formData.fikaAntal) {
        payload['Fikapaket – antal'] = formData.fikaAntal;
        if (formData.fikaVariant) payload['Fikapaket – variant'] = formData.fikaVariant;
        if (formData.fikaPreferenser) payload['Fikapaket – preferenser'] = formData.fikaPreferenser;
        if (formData.fikaWantsDrink === 'Ja') {
          payload['Fikapaket – dryck'] = 'Ja';
          payload['Fikapaket – drycktyp'] = formData.fikaDrinkType || '(Ej angivet)';
        } else {
          payload['Fikapaket – dryck'] = 'Nej';
        }
      }
    }
    if (isCatering) {
      payload['Valda kategorier'] = selectedCategories.length > 0 ? selectedCategories.join(', ') : '(Inga valda)';
      // Per-category preferences
      selectedCategories.forEach(catId => {
        const cat = CATERING_CATEGORIES.find(c => c.id === catId);
        if (cat && categoryNotes[catId]) {
          payload[`${cat.label} – preferenser`] = categoryNotes[catId];
        }
      });
      payload['Typ av event'] = formData.eventType || '(Ej angivet)';
      payload['Antal personer'] = formData.antalPersoner;
      if (formData.cateringDrink === 'Ja') {
        payload['Dryck till eventet'] = 'Ja';
        payload['Dryck – detaljer'] = formData.cateringDrinkDetails || '(Ej angivet)';
      } else {
        payload['Dryck till eventet'] = formData.cateringDrink || '(Ej angivet)';
      }
      if (formData.cateringProjectMgmt === 'Ja') {
        payload['Intresserad av projektledning'] = 'Ja';
        payload['Projektledning – detaljer'] = formData.cateringProjectMgmtDetails || '(Ej angivet)';
      } else {
        payload['Intresserad av projektledning'] = formData.cateringProjectMgmt || 'Nej';
      }
      if (formData.cateringHireStaff === 'Ja') {
        payload['Intresserad av inhyrd personal'] = 'Ja';
        payload['Inhyrd personal – detaljer'] = formData.cateringHireStaffDetails || '(Ej angivet)';
      } else {
        payload['Intresserad av inhyrd personal'] = formData.cateringHireStaff || 'Nej';
      }
    }
    // Allergies with counts
    const allergyDetails = selectedAllergies.filter(a => a !== 'Annat').map(a => {
      const count = allergyCounts[a];
      return count ? `${a} (${count} pers)` : a;
    });
    if (selectedAllergies.includes('Annat')) {
      const annatCount = allergyCounts['Annat'];
      const annatText = formData.allergiAnnat || 'Ej specificerat';
      allergyDetails.push(`Annat: ${annatText}${annatCount ? ` (${annatCount} pers)` : ''}`);
    }
    payload['Allergier'] = allergyDetails.length > 0 ? allergyDetails.join(', ') : '(Inga)';
    payload['Beställningsdetaljer / Meddelande'] = formData.details;
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setIsSuccess(true);
      } else {
        setSubmitError('Något gick fel. Försök igen eller kontakta oss på order@topnotchbakery.se');
      }
    } catch {
      setSubmitError('Kunde inte skicka. Kontrollera din anslutning och försök igen.');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSuccess) {
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-690">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center animate-in zoom-in-95 duration-200" data-id="element-691">
          <div className="w-16 h-16 bg-[#C5A55A]/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-692">
            <Check className="w-8 h-8 text-[#C5A55A]" data-id="element-693" />
          </div>
          <h3 className="text-2xl font-serif text-[#1A1A1A] mb-2" data-id="element-694">
            Tack för din förfrågan!
          </h3>
          <p className="text-[#1A1A1A]/70 mb-8 leading-relaxed" data-id="element-695">
            Vi har tagit emot din offertförfrågan gällande{' '}
            <span className="font-semibold text-[#1A1A1A]" data-id="element-696">{config.title}</span>
            . Vi återkommer till dig med en offert inom 24 timmar.
          </p>
          <Button onClick={onClose} className="w-full" data-id="element-697">
            Stäng fönstret
          </Button>
        </div>
      </div>;
  }
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" onMouseDown={e => e.target === e.currentTarget && onClose()} data-id="element-698">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-4 duration-300" data-id="element-699">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-[#C5A55A]/10 bg-white rounded-t-2xl shrink-0" data-id="element-700">
          <div className="flex items-start gap-4" data-id="element-701">
            <div className="text-4xl bg-[#FFF8F0] w-16 h-16 flex items-center justify-center rounded-xl border border-[#C5A55A]/20 shrink-0" data-id="element-702">
              {config.emoji}
            </div>
            <div data-id="element-703">
              <h3 className="text-2xl font-serif text-[#1A1A1A]" data-id="element-704">
                {config.title}
              </h3>
              <p className="text-[#C5A55A] font-medium text-sm uppercase tracking-wide mb-1" data-id="element-705">
                {config.subtitle}
              </p>
              <p className="text-xs text-[#1A1A1A]/50" data-id="element-706">
                Skickas till order@topnotchbakery.se
              </p>
              <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#C5A55A]/10 text-[#C5A55A] text-xs font-medium rounded-md" data-id="element-707">
                <Clock className="w-3.5 h-3.5" data-id="element-708" />
                Svar inom 24 timmar
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#1A1A1A]/50 hover:text-[#1A1A1A]" aria-label="Stäng" data-id="element-709">
            <X className="w-6 h-6" data-id="element-710" />
          </button>
        </div>

        {/* Form Body */}
        <div className="overflow-y-auto flex-1 p-6 md:p-8" data-id="element-711">
          <form id="order-form" onSubmit={handleSubmit} className="space-y-8" data-id="element-712">
            {/* === MÅNADENS MENY: Display menu === */}
            {isMeny && <div className="space-y-4" data-id="element-713">
                <SectionHeading icon={FileText} title="Månadens meny" data-id="element-714" />
                {MANADENS_MENY_URL ? <div className="rounded-xl border border-[#C5A55A]/20 overflow-hidden bg-white" data-id="element-715">
                    {MANADENS_MENY_URL.endsWith('.pdf') ? <iframe src={MANADENS_MENY_URL} className="w-full h-[500px]" title="Månadens meny" data-id="element-716" /> : <img src={MANADENS_MENY_URL} alt="Månadens meny" className="w-full h-auto" data-id="element-717" />}
                  </div> : <div className="p-8 rounded-xl border-2 border-dashed border-[#C5A55A]/30 bg-[#FFF8F0]/50 text-center" data-id="element-718">
                    <FileText className="w-10 h-10 text-[#C5A55A]/40 mx-auto mb-3" data-id="element-719" />
                    <p className="text-sm text-[#1A1A1A]/50 font-medium" data-id="element-720">
                      Menyn uppdateras varje månad
                    </p>
                    <p className="text-xs text-[#1A1A1A]/40 mt-1" data-id="element-721">
                      Kontakta oss nedan för att få den senaste menyn
                    </p>
                  </div>}
              </div>}

            {/* === FÖRETAGSUPPGIFTER === */}
            {isFöretag && <div className="space-y-4" data-id="element-722">
                <SectionHeading icon={Building2} title="Företagsuppgifter" data-id="element-723" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-724">
                  <div data-id="element-725">
                    <label className={labelClass} data-id="element-726">Företagsnamn {req}</label>
                    <input type="text" name="foretagsnamn" required value={formData.foretagsnamn} onChange={handleChange} className={inputClass} placeholder="Företagets namn" data-id="element-727" />
                  </div>
                  <div data-id="element-728">
                    <label className={labelClass} data-id="element-729">
                      Organisationsnummer {req}
                    </label>
                    <input type="text" name="orgNummer" required value={formData.orgNummer} onChange={handleChange} className={inputClass} placeholder="XXXXXX-XXXX" data-id="element-730" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-731">
                  <div data-id="element-732">
                    <label className={labelClass} data-id="element-733">Faktureringsmejl {req}</label>
                    <input type="email" name="faktureringsEmail" required value={formData.faktureringsEmail} onChange={handleChange} className={inputClass} placeholder="faktura@foretag.se" data-id="element-734" />
                  </div>
                  <div data-id="element-735">
                    <label className={labelClass} data-id="element-736">
                      Referens för bokning {req}
                    </label>
                    <input type="text" name="bokningsReferens" required value={formData.bokningsReferens} onChange={handleChange} className={inputClass} placeholder="T.ex. avdelning, kostnadsställe" data-id="element-737" />
                  </div>
                </div>
              </div>}

            {/* === KONTAKTUPPGIFTER === */}
            <div className="space-y-4" data-id="element-738">
              <SectionHeading icon={User} title="Kontaktuppgifter" data-id="element-739" />
              <div data-id="element-740">
                <label className={labelClass} data-id="element-741">Kontaktperson {req}</label>
                <input type="text" name="kontaktperson" required value={formData.kontaktperson} onChange={handleChange} className={inputClass} placeholder="För- och efternamn" data-id="element-742" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-743">
                <div data-id="element-744">
                  <label className={labelClass} data-id="element-745">E-post {req}</label>
                  <input type="email" name="email" required value={formData.email} onChange={handleChange} className={inputClass} placeholder="din@epost.se" data-id="element-746" />
                </div>
                <div data-id="element-747">
                  <label className={labelClass} data-id="element-748">Telefon {req}</label>
                  <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className={inputClass} placeholder="070-123 45 67" data-id="element-749" />
                </div>
              </div>
            </div>

            {/* === ORDERTYP (not for månadens meny) === */}
            {!isMeny && <div className="space-y-4" data-id="element-750">
                <SectionHeading icon={Repeat} title="Ordertyp" data-id="element-751" />
                <div className="space-y-3" data-id="element-752">
                  <label className={labelClass} data-id="element-753">Typ av order {req}</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-754">
                    {['Engångsbeställning', 'Stående bokning'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-[#FFF8F0] transition-colors has-[:checked]:bg-[#FFF8F0] has-[:checked]:border-[#C5A55A]" data-id="element-755">
                        <input type="radio" name="orderType" value={option} required checked={formData.orderType === option} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-756" />
                        <span className="text-sm text-[#1A1A1A]" data-id="element-757">{option}</span>
                      </label>)}
                  </div>
                </div>

                {/* Engångs: Payment */}
                {isEngångs && <div className="p-4 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10 space-y-4 animate-in slide-in-from-top-2 duration-200" data-id="element-758">
                    <label className={labelClass} data-id="element-759">
                      <CreditCard className="w-4 h-4 inline mr-1.5 text-[#C5A55A]" data-id="element-760" />
                      Betalningssätt {req}
                    </label>
                    <div className="flex flex-col sm:flex-row gap-3" data-id="element-761">
                      {['Betala på plats', 'Faktura'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-white transition-colors has-[:checked]:bg-white has-[:checked]:border-[#C5A55A] flex-1" data-id="element-762">
                          <input type="radio" name="paymentMethod" value={option} required checked={formData.paymentMethod === option} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-763" />
                          <span className="text-sm text-[#1A1A1A]" data-id="element-764">
                            {option}
                          </span>
                        </label>)}
                    </div>
                    {isFaktura && !isFöretag && <div className="space-y-4 pt-2 border-t border-[#C5A55A]/10 mt-3 animate-in slide-in-from-top-1 duration-150" data-id="element-765">
                        <p className="text-xs text-[#1A1A1A]/50 pt-2" data-id="element-766">
                          Fyll i uppgifter för fakturering:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-767">
                          <div data-id="element-768">
                            <label className={labelClass} data-id="element-769">
                              Företagsnamn / Namn {req}
                            </label>
                            <input type="text" name="fakturaForetagsnamn" required value={formData.fakturaForetagsnamn} onChange={handleChange} className={inputClass} placeholder="Fakturamottagare" data-id="element-770" />
                          </div>
                          <div data-id="element-771">
                            <label className={labelClass} data-id="element-772">
                              Org.nr / Personnr {req}
                            </label>
                            <input type="text" name="fakturaOrgNr" required value={formData.fakturaOrgNr} onChange={handleChange} className={inputClass} placeholder="XXXXXX-XXXX" data-id="element-773" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-774">
                          <div data-id="element-775">
                            <label className={labelClass} data-id="element-776">
                              Fakturamejl {req}
                            </label>
                            <input type="email" name="fakturaEmail" required value={formData.fakturaEmail} onChange={handleChange} className={inputClass} placeholder="faktura@epost.se" data-id="element-777" />
                          </div>
                          <div data-id="element-778">
                            <label className={labelClass} data-id="element-779">
                              Fakturaadress {req}
                            </label>
                            <input type="text" name="fakturaAdress" required value={formData.fakturaAdress} onChange={handleChange} className={inputClass} placeholder="Gatuadress, postnr, ort" data-id="element-780" />
                          </div>
                        </div>
                        <div data-id="element-781">
                          <label className={labelClass} data-id="element-782">Referens</label>
                          <input type="text" name="fakturaReferens" value={formData.fakturaReferens} onChange={handleChange} className={inputClass} placeholder="T.ex. kostnadsställe, avdelning" data-id="element-783" />
                        </div>
                      </div>}
                  </div>}

                {/* Stående: Date + Frequency */}
                {isStående && <div className="p-4 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10 space-y-4 animate-in slide-in-from-top-2 duration-200" data-id="element-784">
                    <label className={labelClass} data-id="element-785">
                      <Repeat className="w-4 h-4 inline mr-1.5 text-[#C5A55A]" data-id="element-786" />
                      Återkommande frekvens {req}
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-787">
                      {['Varje vecka', 'Varje månad', 'Specifika dagar'].map(option => <label key={option} className="flex items-center gap-2 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-white transition-colors has-[:checked]:bg-white has-[:checked]:border-[#C5A55A]" data-id="element-788">
                            <input type="radio" name="frequency" value={option} required checked={formData.frequency === option} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-789" />
                            <span className="text-xs text-[#1A1A1A]" data-id="element-790">
                              {option}
                            </span>
                          </label>)}
                    </div>

                    {(formData.frequency === 'Varje vecka' || formData.frequency === 'Varje månad') && <p className="text-xs text-[#1A1A1A]/50 bg-white/60 p-3 rounded-lg border border-[#C5A55A]/10 animate-in slide-in-from-top-1 duration-150" data-id="element-791">
                        📅 Första leveransen bestäms nedan under{' '}
                        <span className="font-semibold" data-id="element-792">Leverans & tid</span>{' '}
                        och återkommer sedan{' '}
                        {formData.frequency === 'Varje vecka' ? 'samma dag varje vecka' : 'samma datum varje månad'}
                        .
                      </p>}

                    {formData.frequency === 'Specifika dagar' && <div className="space-y-3 animate-in slide-in-from-top-1 duration-150" data-id="element-793">
                        <label className={labelClass} data-id="element-794">
                          Vilka dagar vill ni ha leverans? {req}
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-795">
                          {['Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag'].map(day => <label key={day} className={checkboxClass} data-id="element-796">
                              <input type="checkbox" checked={(formData.frequencyDays || '').includes(day)} onChange={() => {
                      const current = formData.frequencyDays ? formData.frequencyDays.split(', ').filter(Boolean) : [];
                      const updated = current.includes(day) ? current.filter(d => d !== day) : [...current, day];
                      setFormData(prev => ({
                        ...prev,
                        frequencyDays: updated.join(', ')
                      }));
                    }} className="w-4 h-4 rounded text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-797" />
                              <span className="text-sm text-[#1A1A1A]" data-id="element-798">
                                {day}
                              </span>
                            </label>)}
                        </div>
                      </div>}
                  </div>}
              </div>}

            {/* === LEVERANS & TID (not for månadens meny) === */}
            {!isMeny && <div className="space-y-4" data-id="element-799">
                <SectionHeading icon={Truck} title="Leverans & tid" data-id="element-800" />

                {isStående && <div className="p-3 bg-[#C5A55A]/5 rounded-lg border border-[#C5A55A]/15 animate-in fade-in duration-200" data-id="element-801">
                    <p className="text-xs text-[#1A1A1A]/60 leading-relaxed" data-id="element-802">
                      ℹ️ Vald tid för avhämtning eller leverans gäller under
                      hela den återkommande frekvensen ni valt ovan.
                    </p>
                  </div>}

                <div className="space-y-3" data-id="element-803">
                  <label className={labelClass} data-id="element-804">Leveranssätt {req}</label>
                  <div className="flex gap-4" data-id="element-805">
                    {['Avhämtning', 'Utkörning'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-[#FFF8F0] transition-colors has-[:checked]:bg-[#FFF8F0] has-[:checked]:border-[#C5A55A] flex-1" data-id="element-806">
                        <input type="radio" name="deliveryType" value={option} checked={formData.deliveryType === option} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-807" />
                        <span className="text-sm text-[#1A1A1A]" data-id="element-808">{option}</span>
                      </label>)}
                  </div>
                </div>
                {isUtkörning && <div className="space-y-3 animate-in slide-in-from-top-1 duration-150" data-id="element-809">
                    {isStående && <p className="text-xs text-[#1A1A1A]/50 bg-white/60 p-3 rounded-lg border border-[#C5A55A]/10" data-id="element-810">
                        🚚 Utkörning för stående bokningar baseras på vald
                        frekvens och ordervärde på beställningen.
                      </p>}
                    <label className={labelClass} data-id="element-811">
                      <MapPin className="w-4 h-4 inline mr-1 text-[#C5A55A]" data-id="element-812" />
                      Leveransadress {req}
                    </label>
                    <p className="text-xs text-[#1A1A1A]/40 mb-1.5" data-id="element-813">
                      Börja skriva för att söka adress
                    </p>
                    <input ref={addressInputRef} type="text" name="deliveryAddress" required value={formData.deliveryAddress} onChange={handleChange} className={inputClass} placeholder="Sök gatuadress, postnummer, ort..." autoComplete="off" data-id="element-814" />
                  </div>}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-815">
                  <div data-id="element-816">
                    <label className={labelClass} data-id="element-817">
                      {isStående ? 'Startdatum (första leveransen)' : 'Önskat datum'}{' '}
                      {req}
                    </label>
                    <div className="relative" data-id="element-818">
                      <input type="date" name="date" required value={formData.date} onChange={handleChange} className={`${inputClass} pl-10`} data-id="element-819" />
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-820" />
                    </div>
                  </div>
                  <div data-id="element-821">
                    <label className={labelClass} data-id="element-822">
                      Önskad tid{' '}
                      {isStående && <span className="text-xs font-normal text-[#1A1A1A]/40 ml-1" data-id="element-823">
                          (gäller alla leveranser)
                        </span>}
                    </label>
                    <div className="relative" data-id="element-824">
                      <input type="time" name="time" value={formData.time} onChange={handleChange} className={`${inputClass} pl-10`} data-id="element-825" />
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-826" />
                    </div>
                  </div>
                </div>
              </div>}

            {/* === FÖRETAG: PAKETVAL === */}
            {isFöretag && <div className="space-y-5" data-id="element-827">
                <SectionHeading icon={ShoppingBag} title="Välj paket" data-id="element-828" />
                <p className="text-xs text-[#1A1A1A]/50 -mt-2" data-id="element-829">
                  Ange antal av varje paket och välj alternativ (1, 2 eller 3).
                  Tryck på info-knappen för att se utbud och priser.
                </p>
                <div className="space-y-4" data-id="element-830">
                  {[{
                key: 'frukost',
                antalKey: 'frukostAntal',
                variantKey: 'frukostVariant',
                label: '🥐 Frukostpaket'
              }, {
                key: 'lunch',
                antalKey: 'lunchAntal',
                variantKey: 'lunchVariant',
                label: '🥗 Lunchpaket'
              }].map(pkg => <div key={pkg.key} className="p-4 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10 space-y-3" data-id="element-831">
                      <div className="flex items-center justify-between" data-id="element-832">
                        <span className="text-sm font-medium text-[#1A1A1A]" data-id="element-833">
                          {pkg.label}
                        </span>
                        <button type="button" onClick={() => setActivePackageInfo(pkg.key)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#C5A55A] bg-[#C5A55A]/10 hover:bg-[#C5A55A]/20 rounded-lg transition-colors" data-id="element-834">
                          <Info className="w-3.5 h-3.5" data-id="element-835" />
                          Se utbud & priser
                        </button>
                      </div>

                      <div className="flex items-center gap-4" data-id="element-836">
                        <label className="text-xs text-[#1A1A1A]/60 shrink-0" data-id="element-837">
                          Antal:
                        </label>
                        <input type="number" name={pkg.antalKey} min="0" value={formData[pkg.antalKey]} onChange={handleChange} className={`${inputClass} max-w-[100px]`} placeholder="0" data-id="element-838" />
                        <span className="text-xs text-[#1A1A1A]/40" data-id="element-839">st</span>
                      </div>

                      <div data-id="element-840">
                        <label className="text-xs text-[#1A1A1A]/60 block mb-2" data-id="element-841">
                          Välj alternativ:
                        </label>
                        <div className="flex gap-3" data-id="element-842">
                          {['1', '2', '3'].map(variant => <label key={variant} className="flex items-center justify-center w-12 h-10 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-[#FFF8F0] transition-colors has-[:checked]:bg-[#C5A55A]/15 has-[:checked]:border-[#C5A55A] has-[:checked]:font-semibold" data-id="element-843">
                              <input type="radio" name={pkg.variantKey} value={variant} checked={formData[pkg.variantKey] === variant} onChange={handleChange} className="sr-only" data-id="element-844" />
                              <span className="text-sm text-[#1A1A1A]" data-id="element-845">
                                {variant}
                              </span>
                            </label>)}
                        </div>
                      </div>
                    </div>)}

                  {/* ☕ Fikapaket – custom descriptive version */}
                  <div className="p-4 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10 space-y-4" data-id="element-846">
                    <div className="flex items-center justify-between" data-id="element-847">
                      <span className="text-sm font-medium text-[#1A1A1A]" data-id="element-848">
                        ☕ Fikapaket
                      </span>
                      <button type="button" onClick={() => setActivePackageInfo('fika')} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#C5A55A] bg-[#C5A55A]/10 hover:bg-[#C5A55A]/20 rounded-lg transition-colors" data-id="element-849">
                        <Info className="w-3.5 h-3.5" data-id="element-850" />
                        Se utbud & priser
                      </button>
                    </div>

                    <div className="flex items-center gap-4" data-id="element-851">
                      <label className="text-xs text-[#1A1A1A]/60 shrink-0" data-id="element-852">
                        Antal:
                      </label>
                      <input type="number" name="fikaAntal" min="0" value={formData.fikaAntal} onChange={handleChange} className={`${inputClass} max-w-[100px]`} placeholder="0" data-id="element-853" />
                      <span className="text-xs text-[#1A1A1A]/40" data-id="element-854">st</span>
                    </div>

                    <div data-id="element-855">
                      <label className="text-xs text-[#1A1A1A]/60 block mb-2" data-id="element-856">
                        Välj typ av fika:
                      </label>
                      <div className="space-y-2" data-id="element-857">
                        {[{
                      value: 'Bullar & kakor',
                      label: 'Bullar & kakor',
                      desc: 'Bullar, kakor, chokladbollar, chokladdoppade bars m.m. (varierar efter dagens utbud)'
                    }, {
                      value: 'Kondisbakelser',
                      label: 'Kondisbakelser',
                      desc: 'Fina bakelser från vårt konditori'
                    }, {
                      value: 'Tårta & morotskaka',
                      label: 'Tårta & morotskaka',
                      desc: 'Morotskaka, tårta och andra större bakverk'
                    }].map(option => <label key={option.value} className="flex items-start gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-[#FFF8F0] transition-colors has-[:checked]:bg-[#FFF8F0] has-[:checked]:border-[#C5A55A]" data-id="element-858">
                            <input type="radio" name="fikaVariant" value={option.value} checked={formData.fikaVariant === option.value} onChange={handleChange} className="w-4 h-4 mt-0.5 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-859" />
                            <div data-id="element-860">
                              <span className="text-sm font-medium text-[#1A1A1A]" data-id="element-861">
                                {option.label}
                              </span>
                              <p className="text-xs text-[#1A1A1A]/50 mt-0.5" data-id="element-862">
                                {option.desc}
                              </p>
                            </div>
                          </label>)}
                      </div>
                    </div>

                    {/* Preferences textarea */}
                    <div data-id="element-863">
                      <label className={labelClass} data-id="element-864">
                        Preferenser eller önskemål
                      </label>
                      <textarea name="fikaPreferenser" value={formData.fikaPreferenser} onChange={handleChange} className={textareaClass} style={{
                    minHeight: '70px'
                  }} placeholder="T.ex. 'Gärna extra chokladbollar', 'Inga nötter', 'Blandning av allt'..." data-id="element-865" />
                    </div>

                    {/* Drink option */}
                    <div className="border-t border-[#C5A55A]/10 pt-4 space-y-3" data-id="element-866">
                      <label className={labelClass} data-id="element-867">
                        Vill ni ha dryck till fikan?
                      </label>
                      <div className="flex gap-3" data-id="element-868">
                        {['Ja', 'Nej'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-[#FFF8F0] transition-colors has-[:checked]:bg-[#FFF8F0] has-[:checked]:border-[#C5A55A] flex-1" data-id="element-869">
                            <input type="radio" name="fikaWantsDrink" value={option} checked={formData.fikaWantsDrink === option} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-870" />
                            <span className="text-sm text-[#1A1A1A]" data-id="element-871">
                              {option}
                            </span>
                          </label>)}
                      </div>

                      {formData.fikaWantsDrink === 'Ja' && <div className="animate-in slide-in-from-top-2 duration-200" data-id="element-872">
                          <label className={labelClass} data-id="element-873">
                            Vilken typ av dryck önskas?
                          </label>
                          <textarea name="fikaDrinkType" value={formData.fikaDrinkType} onChange={handleChange} className={textareaClass} style={{
                      minHeight: '60px'
                    }} placeholder="T.ex. 'Kaffe och te', 'Juice och vatten', 'Blandning av kaffe, te och juice'..." data-id="element-874" />
                        </div>}
                    </div>
                  </div>
                </div>
              </div>}

            {/* Package Info Popup */}
            {activePackageInfo && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-150" onClick={e => {
            if (e.target === e.currentTarget) setActivePackageInfo(null);
          }} data-id="element-875">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col animate-in zoom-in-95 duration-200" data-id="element-876">
                  <div className="flex items-center justify-between p-5 border-b border-[#C5A55A]/10 shrink-0" data-id="element-877">
                    <h4 className="font-serif text-lg text-[#1A1A1A]" data-id="element-878">
                      {activePackageInfo === 'frukost' && '🥐 Frukostpaket'}
                      {activePackageInfo === 'lunch' && '🥗 Lunchpaket'}
                      {activePackageInfo === 'fika' && '☕ Fikapaket'}
                      {' – Utbud & priser'}
                    </h4>
                    <button type="button" onClick={() => setActivePackageInfo(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#1A1A1A]/50 hover:text-[#1A1A1A]" aria-label="Stäng" data-id="element-879">
                      <X className="w-5 h-5" data-id="element-880" />
                    </button>
                  </div>
                  <div className="overflow-y-auto flex-1 p-5" data-id="element-881">
                    {PACKAGE_INFO_URLS[activePackageInfo] ? PACKAGE_INFO_URLS[activePackageInfo].endsWith('.pdf') ? <iframe src={PACKAGE_INFO_URLS[activePackageInfo]} className="w-full h-[500px] rounded-lg" title="Paketinformation" data-id="element-882" /> : <img src={PACKAGE_INFO_URLS[activePackageInfo]} alt="Paketinformation" className="w-full h-auto rounded-lg" data-id="element-883" /> : <div className="p-10 rounded-xl border-2 border-dashed border-[#C5A55A]/30 bg-[#FFF8F0]/50 text-center" data-id="element-884">
                        <FileText className="w-10 h-10 text-[#C5A55A]/40 mx-auto mb-3" data-id="element-885" />
                        <p className="text-sm text-[#1A1A1A]/50 font-medium" data-id="element-886">
                          Information kommer snart
                        </p>
                        <p className="text-xs text-[#1A1A1A]/40 mt-1" data-id="element-887">
                          Kontakta oss på order@topnotchbakery.se för aktuellt
                          utbud och priser
                        </p>
                      </div>}
                  </div>
                  <div className="p-4 border-t border-[#C5A55A]/10 shrink-0" data-id="element-888">
                    <Button type="button" variant="outline" onClick={() => setActivePackageInfo(null)} className="w-full" size="sm" data-id="element-889">
                      Stäng
                    </Button>
                  </div>
                </div>
              </div>}

            {/* === CATERING: KATEGORIER === */}
            {isCatering && <div className="space-y-6" data-id="element-890">
                {/* Event info */}
                <div className="space-y-4" data-id="element-891">
                  <SectionHeading icon={UtensilsCrossed} title="Om ert event" data-id="element-892" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-893">
                    <div data-id="element-894">
                      <label className={labelClass} data-id="element-895">Typ av event {req}</label>
                      <input type="text" name="eventType" required value={formData.eventType} onChange={handleChange} className={inputClass} placeholder="T.ex. bröllop, företagsfest, mingel, begravning" data-id="element-896" />
                    </div>
                    <div data-id="element-897">
                      <label className={labelClass} data-id="element-898">Antal personer {req}</label>
                      <input type="number" name="antalPersoner" required min="1" value={formData.antalPersoner} onChange={handleChange} className={inputClass} placeholder="Ungefärligt antal" data-id="element-899" />
                    </div>
                  </div>
                </div>

                {/* What to order */}
                <div className="space-y-4" data-id="element-900">
                  <SectionHeading icon={ShoppingBag} title="Vad vill ni beställa?" data-id="element-901" />
                  <p className="text-xs text-[#1A1A1A]/50 -mt-2" data-id="element-902">
                    Bocka i det som är intressant. Beskriv sedan mer i detalj
                    längre ner.
                  </p>
                  <div className="space-y-2" data-id="element-903">
                    {CATERING_CATEGORIES.map(cat => <div key={cat.id} data-id="element-904">
                        <label className="flex items-start gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-[#FFF8F0] transition-colors has-[:checked]:bg-[#FFF8F0] has-[:checked]:border-[#C5A55A]" data-id="element-905">
                          <input type="checkbox" checked={selectedCategories.includes(cat.id)} onChange={() => toggleCategory(cat.id)} className="w-4 h-4 mt-0.5 rounded text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-906" />
                          <div data-id="element-907">
                            <span className="text-sm font-medium text-[#1A1A1A]" data-id="element-908">
                              {cat.label}
                            </span>
                            <p className="text-xs text-[#1A1A1A]/50 mt-0.5" data-id="element-909">
                              {cat.desc}
                            </p>
                          </div>
                        </label>
                        {selectedCategories.includes(cat.id) && <div className="ml-7 mt-2 mb-1 animate-in slide-in-from-top-1 duration-150" data-id="element-910">
                            <textarea value={categoryNotes[cat.id] || ''} onChange={e => setCategoryNotes(prev => ({
                      ...prev,
                      [cat.id]: e.target.value
                    }))} className={textareaClass} style={{
                      minHeight: '60px'
                    }} placeholder={`Beskriv kort era önskemål för ${cat.label.replace(/^[^\s]+\s/, '')}...`} data-id="element-911" />
                          </div>}
                      </div>)}
                  </div>
                </div>

                {/* Drink */}
                <div className="space-y-3 p-4 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10" data-id="element-912">
                  <label className={labelClass} data-id="element-913">
                    Vill ni ha dryck till eventet?
                  </label>
                  <div className="flex gap-3" data-id="element-914">
                    {['Ja', 'Nej'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-white transition-colors has-[:checked]:bg-white has-[:checked]:border-[#C5A55A] flex-1" data-id="element-915">
                        <input type="radio" name="cateringDrink" value={option} checked={formData.cateringDrink === option} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-916" />
                        <span className="text-sm text-[#1A1A1A]" data-id="element-917">{option}</span>
                      </label>)}
                  </div>
                  {formData.cateringDrink === 'Ja' && <div className="animate-in slide-in-from-top-2 duration-200" data-id="element-918">
                      <label className={labelClass} data-id="element-919">
                        Vilken typ av dryck önskas?
                      </label>
                      <textarea name="cateringDrinkDetails" value={formData.cateringDrinkDetails} onChange={handleChange} className={textareaClass} style={{
                  minHeight: '60px'
                }} placeholder="T.ex. kaffe, te, juice, läsk, vatten..." data-id="element-920" />
                    </div>}
                </div>

                {/* Extra services */}
                <div className="space-y-4" data-id="element-921">
                  <SectionHeading icon={User} title="Extratjänster" data-id="element-922" />
                  <p className="text-xs text-[#1A1A1A]/50 -mt-2" data-id="element-923">
                    Vi kan hjälpa till med mer än bara mat och fika.
                  </p>

                  {/* Project management */}
                  <div className="p-4 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10 space-y-3" data-id="element-924">
                    <div data-id="element-925">
                      <span className="text-sm font-medium text-[#1A1A1A]" data-id="element-926">
                        📋 Projektledning av eventet
                      </span>
                      <p className="text-xs text-[#1A1A1A]/50 mt-0.5" data-id="element-927">
                        Vi hjälper er planera och koordinera mat, logistik och
                        upplägg för ert event.
                      </p>
                    </div>
                    <div className="flex gap-3" data-id="element-928">
                      {['Ja, intresserad', 'Nej tack'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-white transition-colors has-[:checked]:bg-white has-[:checked]:border-[#C5A55A] flex-1" data-id="element-929">
                          <input type="radio" name="cateringProjectMgmt" value={option === 'Ja, intresserad' ? 'Ja' : 'Nej'} checked={formData.cateringProjectMgmt === (option === 'Ja, intresserad' ? 'Ja' : 'Nej')} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-930" />
                          <span className="text-sm text-[#1A1A1A]" data-id="element-931">
                            {option}
                          </span>
                        </label>)}
                    </div>
                    {formData.cateringProjectMgmt === 'Ja' && <div className="animate-in slide-in-from-top-2 duration-200" data-id="element-932">
                        <label className={labelClass} data-id="element-933">
                          Berätta kort vad ni behöver hjälp med
                        </label>
                        <textarea name="cateringProjectMgmtDetails" value={formData.cateringProjectMgmtDetails} onChange={handleChange} className={textareaClass} style={{
                    minHeight: '60px'
                  }} placeholder="T.ex. 'Vi behöver hjälp med planering av meny, uppdukning och tidsschema för kvällen'" data-id="element-934" />
                      </div>}
                  </div>

                  {/* Hire staff */}
                  <div className="p-4 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10 space-y-3" data-id="element-935">
                    <div data-id="element-936">
                      <span className="text-sm font-medium text-[#1A1A1A]" data-id="element-937">
                        👥 Hyra in personal
                      </span>
                      <p className="text-xs text-[#1A1A1A]/50 mt-0.5" data-id="element-938">
                        Behöver ni servicepersonal, serveringspersonal eller
                        annan hjälp på plats under eventet?
                      </p>
                    </div>
                    <div className="flex gap-3" data-id="element-939">
                      {['Ja, intresserad', 'Nej tack'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-white transition-colors has-[:checked]:bg-white has-[:checked]:border-[#C5A55A] flex-1" data-id="element-940">
                          <input type="radio" name="cateringHireStaff" value={option === 'Ja, intresserad' ? 'Ja' : 'Nej'} checked={formData.cateringHireStaff === (option === 'Ja, intresserad' ? 'Ja' : 'Nej')} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-941" />
                          <span className="text-sm text-[#1A1A1A]" data-id="element-942">
                            {option}
                          </span>
                        </label>)}
                    </div>
                    {formData.cateringHireStaff === 'Ja' && <div className="animate-in slide-in-from-top-2 duration-200" data-id="element-943">
                        <label className={labelClass} data-id="element-944">
                          Berätta kort vad ni behöver
                        </label>
                        <textarea name="cateringHireStaffDetails" value={formData.cateringHireStaffDetails} onChange={handleChange} className={textareaClass} style={{
                    minHeight: '60px'
                  }} placeholder="T.ex. '2 serveringspersonal under 4 timmar', 'Personal som sköter uppdukning och servering'" data-id="element-945" />
                      </div>}
                  </div>
                </div>
              </div>}

            {/* === ALLERGIER (företag & catering) === */}
            {(isFöretag || isCatering) && <div className="space-y-4" data-id="element-946">
                <SectionHeading icon={AlertTriangle} title="Allergier & specialkost" data-id="element-947" />
                <p className="text-xs text-[#1A1A1A]/50 -mt-2" data-id="element-948">
                  Bocka i eventuella allergier och ange hur många personer det
                  gäller.
                </p>
                <div className="space-y-3" data-id="element-949">
                  {ALLERGIES.map(allergy => <div key={allergy} data-id="element-950">
                      <label className={`${checkboxClass} ${selectedAllergies.includes(allergy) ? '' : ''}`} data-id="element-951">
                        <input type="checkbox" checked={selectedAllergies.includes(allergy)} onChange={() => toggleAllergy(allergy)} className="w-4 h-4 rounded text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-952" />
                        <span className="text-sm text-[#1A1A1A] flex-1" data-id="element-953">
                          {allergy}
                        </span>
                        {selectedAllergies.includes(allergy) && allergy !== 'Annat' && <div className="flex items-center gap-2 ml-auto" onClick={e => e.preventDefault()} data-id="element-954">
                              <input type="number" min="1" value={allergyCounts[allergy] || ''} onChange={e => {
                      e.stopPropagation();
                      setAllergyCount(allergy, e.target.value);
                    }} onClick={e => e.stopPropagation()} className="w-16 h-8 px-2 text-center rounded-md border border-[#C5A55A]/30 bg-white text-sm focus:border-[#C5A55A] focus:ring-1 focus:ring-[#C5A55A]/20 outline-none" placeholder="0" data-id="element-955" />
                              <span className="text-xs text-[#1A1A1A]/40 whitespace-nowrap" data-id="element-956">
                                pers
                              </span>
                            </div>}
                      </label>
                      {selectedAllergies.includes(allergy) && allergy === 'Annat' && <div className="mt-2 ml-9 flex items-center gap-3 animate-in slide-in-from-top-1 duration-150" data-id="element-957">
                            <input type="text" name="allergiAnnat" value={formData.allergiAnnat} onChange={handleChange} className={`${inputClass} flex-1`} placeholder="T.ex. sojafri, sesamfri..." data-id="element-958" />
                            <div className="flex items-center gap-2 shrink-0" data-id="element-959">
                              <input type="number" min="1" value={allergyCounts['Annat'] || ''} onChange={e => setAllergyCount('Annat', e.target.value)} className="w-16 h-8 px-2 text-center rounded-md border border-[#C5A55A]/30 bg-white text-sm focus:border-[#C5A55A] focus:ring-1 focus:ring-[#C5A55A]/20 outline-none" placeholder="0" data-id="element-960" />
                              <span className="text-xs text-[#1A1A1A]/40" data-id="element-961">
                                pers
                              </span>
                            </div>
                          </div>}
                    </div>)}
                </div>
              </div>}

            {/* === BESTÄLLNING / MEDDELANDE === */}
            <div className="space-y-4" data-id="element-962">
              <SectionHeading icon={MessageSquare} title={isMeny ? 'Meddelande' : 'Beskriv din beställning'} data-id="element-963" />
              <div data-id="element-964">
                <label className={labelClass} data-id="element-965">
                  {isMeny ? 'Vad vill du veta mer om?' : isFöretag ? 'Beskriv vad ni är sugna på' : 'Beskriv vad ni önskar och ställ frågor'}{' '}
                  {req}
                </label>
                <p className="text-xs text-[#1A1A1A]/50 mb-2" data-id="element-966">
                  {isMeny ? 'Ställ frågor om månadens meny, allergier, eller berätta vad du är intresserad av.' : isFöretag ? 'Berätta vad ni tänker er i varje paket, önskemål om innehåll, eller andra frågor.' : 'Beskriv vad ni tänker er, ställ frågor, eller berätta om särskilda önskemål.'}
                </p>
                <textarea name="details" required value={formData.details} onChange={handleChange} className={textareaClass} placeholder={isMeny ? 'Ditt meddelande...' : 'Beskriv din beställning här...'} data-id="element-967" />
              </div>
            </div>

            {submitError && <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-968">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-969" />
                <p data-id="element-970">{submitError}</p>
              </div>}
          </form>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#C5A55A]/10 bg-white rounded-b-2xl shrink-0 flex justify-between items-center gap-4" data-id="element-971">
          <Button variant="ghost" onClick={onClose} type="button" className="text-[#1A1A1A]/60 hover:text-[#1A1A1A]" data-id="element-972">
            Avbryt
          </Button>
          <Button type="submit" form="order-form" isLoading={isSubmitting} className="min-w-[160px]" data-id="element-973">
            <Send className="w-4 h-4 mr-2" data-id="element-974" />
            Skicka offertförfrågan
          </Button>
        </div>
      </div>
    </div>;
}